import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { merge, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../core/reducers';
import { QueryParamsModel } from '../../../../../core/_base/crud';
import * as moment from 'moment';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { InterventionHistory } from '../../models';
import { InterventionHistoryDataSource } from '../../data-sources/intervention-history.datasource';
import { InterventionHistoryPageRequested } from '../../actions/intervention-history.actions';
import { PendProblemComponent } from '../pend-problem/pend-problem.component';
import { CategoryService } from '../../../../../core/lists';
import { ManageProblemComponent } from '../manage-problem/manage-problem.component';
import { TaskService } from '../../../../../core/timeline';
import { TaskMode } from '../../../calendar/enums';
import { CreateTaskComponent } from '../../../calendar/create-task/create-task.component';

@Component({
  selector: 'kt-intervention-history-list',
  templateUrl: './intervention-history-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InterventionHistoryListComponent implements OnInit, OnDestroy {
  dataSource: InterventionHistoryDataSource;
  displayedColumns = [
    'interventionCode_level_level',
    'team_responsible',
    'interventionNote',
    'acceptanceResult_implementation',
    'drp_outcomeCode',
    'startDate',
    'stopDate',
  ];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  interventionHistoryResult: InterventionHistory[] = [];
  private subscriptions: Subscription[] = [];
  lastQuery: QueryParamsModel;
  private categoryIds = [];
  private teams = [];
  entitiesIds = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<InterventionHistoryListComponent>,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private store: Store<AppState>,
    public stringUtils: StringUtilsService,
    private taskService: TaskService,
    private categoryService: CategoryService,
  ) {}

  ngOnInit() {
    const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subscriptions.push(sortSubscription);

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadInterventionHistoryList()))
      .subscribe();
    this.subscriptions.push(paginatorSubscriptions);
    this.dataSource = new InterventionHistoryDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((res) => {
        this.interventionHistoryResult = res;
        this.interventionHistoryResult.forEach((element) =>
          this.categoryIds.push(element.team_responsible),
        );
        if (this.categoryIds.length > 0)
          this.categoryService.getTeamByIds(this.categoryIds).subscribe((result) => {
            this.teams = result.data;
          });
      });
    this.subscriptions.push(entitiesSubscription);
    this.loadInterventionHistoryList();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => el.unsubscribe());
  }

  loadInterventionHistoryList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction !== '' ? this.sort.direction : 'desc',
      !this.sort.active || this.sort.active === '' ? 'created' : this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    this.store.dispatch(
      new InterventionHistoryPageRequested({
        page: queryParams,
        patient_id: this.data.patient.id,
        drp_id: this.data.drp.id,
      }),
    );
  }

  filterConfiguration(): any {
    return {};
  }

  getDate(date) {
    return moment(date).format('l');
  }

  openInterventionTask(interventionTask) {
    this.taskService
      .getTaskV3(interventionTask.task_id)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res?.data) return;

        this.dialog.open(CreateTaskComponent, {
          disableClose: true,
          closeOnNavigation: true,
          autoFocus: true,
          width: '95vw',
          maxWidth: '95vw',
          data: { mode: TaskMode.VIEW, task: res.data },
        });
      });
  }

  groupBy(list, keyGetter) {
    const map = new Map();
    list.forEach((item) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  }
  close() {
    this.dialogRef.close();
  }

  pendProblem(drp) {
    this.dialog.open(PendProblemComponent, {
      data: {
        patient: this.data.patient,
        drp,
        from: 'later_date',
      },
      width: '75vw',
      maxWidth: '75vw',
      height: '85vh',
    });
  }

  ignore(drp) {
    this.dialog.open(PendProblemComponent, {
      data: {
        patient: this.data.patient,
        drp,
        from: 'ignore',
      },
      width: '65vw',
      maxWidth: '75vw',
      height: '65vh',
    });
  }

  manageProblem() {
    this.dialog.open(ManageProblemComponent, {
      data: {
        patient: this.data.patient,
        drp: this.data.drp,
        from: 'manage',
        facility_cycle_date: this.data.patient.facility_cycle_date,
      },
      width: '75vw',
      maxWidth: '75vw',
      height: '85vh',
    });
  }

  getTeam(value) {
    if (value) {
      if (this.teams.filter((item) => item.id === value)[0]) {
        return this.teams.filter((item) => item.id === value)[0].name;
      }
    }
  }
}
